import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as RadixLabel from '@radix-ui/react-label';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { publicPost } from 'helpers/requests';
import {
  inputCls,
  labelClass,
  errorTextClass,
} from 'modules/Common/form/v2/primitives/inputClasses';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import PoweredByCarbonGlance from 'modules/Embed/PoweredByCarbonGlance';
import { useBranding } from 'modules/App/hooks/useBranding';
import DiagramFooterPanel from 'modules/Diagrams/DiagramEditor/DiagramFooterPanel';
import { apiErrorMessage } from 'helpers/serverIssues';
import { lazyWithRetry } from 'modules/Common/utils/lazyWithRetry';
import { trackPreload } from 'modules/Common/utils/chunkLoadState';

// @xyflow/react (the `flow` chunk, 298 kB raw / 99 kB gzip) lives only under
// Diagrams/, but this page is reachable from an EAGER surface, so a static
// import of the canvas pinned the chunk into the entry graph for every visitor
// regardless of the lazy Diagrams route. Loaded on demand instead.
//
// The Suspense boundary is ContentWrapper's (_app.jsx), which every route
// surface renders inside, so no boundary is needed here.
//
// The page starts this import on mount, so the canvas downloads while the
// visitor types the password instead of after it.
// evidence-attach-ux D17: the public share link shows no paperclip. Module-level so the prop is stable.
const PUBLIC_CONFIG = { showEvidence: false };

const loadCanvas = () => import('modules/Diagrams/components/ReadOnlyDiagramCanvas');
const ReadOnlyDiagramCanvas = lazyWithRetry(loadCanvas);

function PasswordForm({ onSuccess }) {
  const { id } = useParams();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Ensure brand CSS variables (--primary-color etc.) are applied — the
  // public page bypasses ContentWrapper's hook which normally triggers this.
  useEffect(() => {
    useBranding.getState().applyCss();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await publicPost(`/public/diagrams/${id}`, { password });
      onSuccess(res.data);
    } catch (err) {
      setError(apiErrorMessage(err) || 'Invalid password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-full bg-primaryColor/10 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faLock}
                className="text-primaryColor text-sm"
              />
            </div>
            <h1 className="text-base font-semibold text-slate-800 m-0">Access Diagram</h1>
          </div>
          <p className="text-xs text-slate-400 mt-1 ml-11">
            Enter the password to view this diagram.
          </p>
        </div>

        {/* Form body */}
        <form
          onSubmit={handleSubmit}
          className="px-6 py-5 flex flex-col gap-4"
        >
          <div>
            <RadixLabel.Root
              htmlFor="diagram-password"
              className={labelClass}
            >
              Password
            </RadixLabel.Root>
            <input
              id="diagram-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              placeholder="Enter password"
              required
              autoFocus
              className={inputCls(!!error, 'mt-1')}
            />
            {error && (
              <p className={`${errorTextClass} flex items-center gap-1.5 mt-1.5`}>
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="shrink-0"
                />
                {error}
              </p>
            )}
          </div>

          <CgRadixButton
            type="submit"
            variant="primary"
            size="md"
            disabled={!password}
            loading={loading}
            loadingLabel="Verifying…"
            className="w-full justify-center"
          >
            View Diagram
          </CgRadixButton>
        </form>
      </div>
    </div>
  );
}

export default function PublicDiagramPage() {
  const [diagram, setDiagram] = useState(null);

  useEffect(() => {
    trackPreload(loadCanvas()).catch(() => {});
  }, []);

  if (!diagram) {
    return <PasswordForm onSuccess={setDiagram} />;
  }
  return (
    <ReadOnlyDiagramCanvas
      nodes={diagram.nodes}
      edges={diagram.edges}
      reportingPeriod={diagram.reportingPeriod}
      diagramId={diagram?._id}
      configOverrides={PUBLIC_CONFIG}
      style={{ width: '100vw', height: '100vh' }}
    >
      {(diagram.installationName || diagram.reportingPeriod) && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <DiagramFooterPanel
            installationId={diagram.installationId}
            installationName={diagram.installationName}
            reportingPeriod={diagram.reportingPeriod}
            reportingPeriodInteractive={false}
          />
        </div>
      )}

      <div className="absolute bottom-4 right-4 z-10">
        <PoweredByCarbonGlance
          utmMedium="shared-diagram"
          variant="cta"
        />
      </div>
    </ReadOnlyDiagramCanvas>
  );
}
